import { useEffect, useRef } from 'react';
import styles from './SlcFreeTextPopover.module.css';

/**
 * Small overlay for slide-level free-text entry (hybrid or free-text-only multiline).
 */
export function SlcFreeTextPopover({
    open,
    onClose,
    multiline,
    placeholder,
    value,
    onChangeValue,
    title = 'Free-text answer',
}) {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const id = requestAnimationFrame(() => {
            const el = panelRef.current?.querySelector('textarea, input[type="text"]');
            el?.focus?.();
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    if (!open) return null;

    function onBackdropMouseDown(e) {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }

    return (
        <div
            className={styles.backdrop}
            role="presentation"
            data-slc-free-text-popover
            onMouseDown={onBackdropMouseDown}
        >
            <div
                ref={panelRef}
                className={styles.panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="slc-ft-popover-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <h2 id="slc-ft-popover-title" className={styles.title}>
                    {title}
                </h2>
                {multiline ? (
                    <textarea
                        className={styles.textarea}
                        rows={5}
                        value={value}
                        placeholder={placeholder || undefined}
                        onChange={(e) => onChangeValue(e.target.value)}
                        aria-label={placeholder || title}
                    />
                ) : (
                    <input
                        type="text"
                        className={styles.input}
                        value={value}
                        placeholder={placeholder || undefined}
                        onChange={(e) => onChangeValue(e.target.value)}
                        aria-label={placeholder || title}
                    />
                )}
                <div className={styles.actions}>
                    <button type="button" className={styles.doneButton} onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
